import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../auth/AuthContext';
import { useCompany } from '../Company/CompanyContext';
import EosLogo from '../layout/EosLogo';
import CelebrationModal from './CelebrationModal';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';
import { generateServerStateToken } from '../../services/authStateService';
import { getAuth, updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { toast } from 'react-toastify';
import LordIcon from '../common/LordIcon';
import autorenewIcon from '../../assets/icons/autorenew.json';

function SettingsModal({ isOpen, onClose }) {
  // Tab state
  const [activeTab, setActiveTab] = useState('company');
  
  // Form data
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [size, setSize] = useState('');
  const [description, setDescription] = useState('');
  const [mission, setMission] = useState('');
  const [customLogoUrl, setCustomLogoUrl] = useState('');
  
  // Google Calendar integration state
  const [isCalendarConnected, setIsCalendarConnected] = useState(false);
  const [isCalendarConnecting, setIsCalendarConnecting] = useState(false);
  const [calendarTokens, setCalendarTokens] = useState(null);
  
  // Google Gmail integration state
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [isGmailConnecting, setIsGmailConnecting] = useState(false);
  const [gmailTokens, setGmailTokens] = useState(null);
  
  // UI states
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [blurApplied, setBlurApplied] = useState(false);
  const [backdropVisible, setBackdropVisible] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showReloadButton, setShowReloadButton] = useState(false);
  const modalRef = useRef(null);
  
  const { userProfile } = useAuth();
  const { companyInfo, updateCompanyInfo, loading: companyLoading } = useCompany();
  
  // Handle actual rendering, blur, and visibility with proper animation timing
  useEffect(() => {
    let blurTimer, visibilityTimer, backdropTimer, renderTimer;
    
    if (isOpen) {
      // First render the component
      setShouldRender(true);
      
      // Apply blur immediately but keep opacity at 0
      blurTimer = setTimeout(() => {
        setBlurApplied(true);
        
        // Wait for blur to be fully applied before showing backdrop
        backdropTimer = setTimeout(() => {
          setBackdropVisible(true);
          
          // Only after backdrop and blur are ready, fade in content
          visibilityTimer = setTimeout(() => {
            setIsVisible(true);
          }, 30);
        }, 20);
      }, 5);
    } else {
      // First fade out content
      setIsVisible(false);
      
      // After content fades, start fading backdrop
      backdropTimer = setTimeout(() => {
        setBackdropVisible(false);
        
        // Then remove blur effect
        blurTimer = setTimeout(() => {
          setBlurApplied(false);
          
          // Finally remove from DOM after all animations complete
          renderTimer = setTimeout(() => {
            setShouldRender(false);
          }, 50);
        }, 80);
      }, 150);
    }
    
    // Cleanup all timers on unmount
    return () => {
      clearTimeout(blurTimer);
      clearTimeout(visibilityTimer);
      clearTimeout(backdropTimer);
      clearTimeout(renderTimer);
    };
  }, [isOpen]);
  
  // Handle click outside to close
  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleClose();
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  // Load company data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // If companyInfo is available from context, use it
        if (companyInfo) {
          setCompanyName(companyInfo.name || '');
          setIndustry(companyInfo.industry || '');
          setSize(companyInfo.size || '');
          setDescription(companyInfo.description || '');
          setMission(companyInfo.mission || '');
          setCustomLogoUrl(companyInfo.customLogoUrl || '');
          
          // Load calendar connection status
          if (companyInfo.googleCalendar) {
            setIsCalendarConnected(true);
            setCalendarTokens(companyInfo.googleCalendar);
          } else {
            setIsCalendarConnected(false);
            setCalendarTokens(null);
          }
          
          // Load Gmail connection status
          if (companyInfo.googleGmail) {
            setIsGmailConnected(true);
            setGmailTokens(companyInfo.googleGmail);
          } else {
            setIsGmailConnected(false);
            setGmailTokens(null);
          }
          
          setLoading(false);
          return;
        }
        
        // If userProfile has company ID but no companyInfo, fetch it directly
        if (userProfile && userProfile.company) {
          try {
            const companyRef = doc(db, "companies", userProfile.company);
            const companySnap = await getDoc(companyRef);
            
            if (companySnap.exists()) {
              const data = companySnap.data();
              setCompanyName(data.name || '');
              setIndustry(data.industry || '');
              setSize(data.size || '');
              setDescription(data.description || '');
              setMission(data.mission || '');
              setCustomLogoUrl(data.customLogoUrl || '');
              
              // Load calendar connection status
              if (data.googleCalendar) {
                setIsCalendarConnected(true);
                setCalendarTokens(data.googleCalendar);
              } else {
                setIsCalendarConnected(false);
                setCalendarTokens(null);
              }
              
              // Load Gmail connection status
              if (data.googleGmail) {
                setIsGmailConnected(true);
                setGmailTokens(data.googleGmail);
              } else {
                setIsGmailConnected(false);
                setGmailTokens(null);
              }
            }
          } catch (fetchErr) {
            console.error("Error fetching company:", fetchErr);
            setError("Failed to load company data");
          }
        } else {
          // No company info available - set defaults
          setCompanyName('');
          setIndustry('');
          setSize('');
          setDescription('');
          setMission('');
          setCustomLogoUrl('');
          setIsCalendarConnected(false);
          setCalendarTokens(null);
          setIsGmailConnected(false);
          setGmailTokens(null);
        }
      } catch (err) {
        console.error("Error loading company data:", err);
        setError("Failed to load company information");
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) {
      loadData();
    }
  }, [companyInfo, userProfile, companyLoading, isOpen]);
  
  // Listen for successful connection events
  useEffect(() => {
    const handleGoogleServiceConnected = (event) => {
      // Make sure we have the detail with service and data
      if (!event.detail || !event.detail.service) return;
      
      const { service, data } = event.detail;
      
      if (service === 'calendar' && data) {
        console.log('Received calendar connection event');
        setIsCalendarConnected(true);
        setCalendarTokens(data);
        setSuccess('Google Calendar connected successfully');
      } 
      else if (service === 'gmail' && data) {
        console.log('Received gmail connection event');
        setIsGmailConnected(true);
        setGmailTokens(data);
        setSuccess('Google Gmail connected successfully');
      }
    };
    
    // Add the event listener
    window.addEventListener('google-service-connected', handleGoogleServiceConnected);
    
    // Cleanup
    return () => {
      window.removeEventListener('google-service-connected', handleGoogleServiceConnected);
    };
  }, []);
  
  // Handle logo file upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Clear previous error messages
    setError('');
    
    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!validImageTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, or SVG)');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo file size should be less than 2MB');
      return;
    }
    
    try {
      setIsLogoUploading(true);
      
      // Check if storage is available
      if (!storage) {
        throw new Error('Firebase Storage is not initialized');
      }
      
      // Determine company ID
      let companyId = 'default';
      if (userProfile && userProfile.company) {
        companyId = userProfile.company;
      } else if (userProfile && userProfile.uid) {
        // If no company ID, use user ID as fallback
        companyId = `user_${userProfile.uid}`;
      }
      
      console.log('Uploading logo with company ID:', companyId);
      
      // Create a unique filename to prevent overwriting
      const timestamp = new Date().getTime();
      const safeFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
      const fileName = `${timestamp}_${safeFileName}`;
      
      // Create a reference to the storage location
      const storagePath = `company-logos/${companyId}/${fileName}`;
      console.log('Storage path:', storagePath);
      
      const logoRef = ref(storage, storagePath);
      
      // Upload the file to Firebase Storage
      console.log('Starting upload...');
      const uploadResult = await uploadBytes(logoRef, file);
      
      if (!uploadResult || !uploadResult.ref) {
        throw new Error('Upload failed - no reference returned');
      }
      
      console.log('Upload successful, getting download URL...');
      
      // Get the download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      if (!downloadURL) {
        throw new Error('Failed to get download URL');
      }
      
      console.log('Got download URL:', downloadURL);
      
      // Update the customLogoUrl state
      setCustomLogoUrl(downloadURL);
      setSuccess('Logo uploaded successfully. Save changes to update your company profile.');
      
    } catch (err) {
      console.error('Error uploading logo:', err);
      
      let errorMessage = 'Failed to upload logo';
      if (err.code === 'storage/unauthorized') {
        errorMessage += ': You don\'t have permission to upload to this location';
      } else if (err.code) {
        errorMessage += `: ${err.code}`;
      } else if (err.message) {
        errorMessage += `: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setIsLogoUploading(false);
    }
  };
  
  // Handle Google Calendar authentication
  const handleConnectGoogleCalendar = async () => {
    try {
      setIsCalendarConnecting(true);
      setError('');
      
      // Generate a server-side state token instead of relying on browser storage
      console.log('Generating server-side state token...');
      const stateToken = await generateServerStateToken();
      console.log('Server state token generated');
      
      // 2. Set up OAuth parameters
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!clientId) {
        throw new Error('Google Client ID is not configured');
      }
      
      console.log('Using Client ID:', clientId.substring(0, 8) + '...');
      
      // 3. Define the required scopes - request read-only access to calendar
      const scopes = [
        'https://www.googleapis.com/auth/calendar.readonly',
        'https://www.googleapis.com/auth/calendar.events.readonly'
      ];
      
      // 4. Set up the redirect URI
      const redirectUri = `${window.location.origin}/auth/google-calendar-callback`;
      console.log('Using redirect URI:', redirectUri);
      
      // 5. Construct the authorization URL
      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      
      // 6. Add required parameters
      authUrl.searchParams.append('client_id', clientId);
      authUrl.searchParams.append('redirect_uri', redirectUri);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('scope', scopes.join(' '));
      authUrl.searchParams.append('access_type', 'offline');
      authUrl.searchParams.append('state', stateToken);
      authUrl.searchParams.append('prompt', 'consent'); // Force to get refresh token
      
      // 7. Open the authorization URL in a new window or redirect
      console.log('Redirecting to Google OAuth page...');
      window.location.href = authUrl.toString();
      
    } catch (err) {
      console.error('Error connecting to Google Calendar:', err);
      setError(`Failed to connect to Google Calendar: ${err.message}`);
      setIsCalendarConnecting(false);
    }
  };
  
  // Handle disconnecting Google Calendar
  const handleDisconnectGoogleCalendar = async () => {
    try {
      setIsCalendarConnecting(true);
      setError('');
      
      // Check if there's a company profile
      if (!userProfile || !userProfile.company) {
        throw new Error('No company profile found');
      }
      
      // Get a reference to the company document
      const companyRef = doc(db, "companies", userProfile.company);
      
      // Update the document to remove the googleCalendar field
      await updateDoc(companyRef, {
        googleCalendar: deleteField()
      });
      
      // Update the company data in the context
      if (updateCompanyInfo) {
        await updateCompanyInfo();
      }
      
      setSuccess('Google Calendar disconnected successfully. Please reload the page to see the changes.');
      setShowReloadButton(true);
      setIsCalendarConnecting(false);
    } catch (err) {
      console.error('Error disconnecting Google Calendar:', err);
      setError(`Failed to disconnect Google Calendar: ${err.message}`);
      setIsCalendarConnecting(false);
    }
  };
  
  // Handle Google Gmail authentication
  const handleConnectGoogleGmail = async () => {
    try {
      setIsGmailConnecting(true);
      setError('');
      
      // Generate a server-side state token instead of relying on browser storage
      console.log('Generating server-side state token...');
      const stateToken = await generateServerStateToken();
      console.log('Server state token generated');
      
      // 2. Set up OAuth parameters
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!clientId) {
        throw new Error('Google Client ID is not configured');
      }
      
      console.log('Using Client ID:', clientId.substring(0, 8) + '...');
      
      // 3. Define the required scopes - request read-only access to Gmail
      const scopes = [
        'https://www.googleapis.com/auth/gmail.readonly'
      ];
      
      // 4. Set up the redirect URI
      const redirectUri = `${window.location.origin}/auth/google-gmail-callback`;
      console.log('Using redirect URI:', redirectUri);
      
      // 5. Construct the authorization URL
      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      
      // 6. Add required parameters
      authUrl.searchParams.append('client_id', clientId);
      authUrl.searchParams.append('redirect_uri', redirectUri);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('scope', scopes.join(' '));
      authUrl.searchParams.append('access_type', 'offline');
      authUrl.searchParams.append('state', stateToken);
      authUrl.searchParams.append('prompt', 'consent'); // Force to get refresh token
      
      // 7. Open the authorization URL in a new window or redirect
      console.log('Redirecting to Google OAuth page for Gmail...');
      window.location.href = authUrl.toString();
      
    } catch (err) {
      console.error('Error connecting to Google Gmail:', err);
      setError(`Failed to connect to Google Gmail: ${err.message}`);
      setIsGmailConnecting(false);
    }
  };
  
  // Handle disconnecting Google Gmail
  const handleDisconnectGoogleGmail = async () => {
    try {
      setIsGmailConnecting(true);
      setError('');
      
      // Check if there's a company profile
      if (!userProfile || !userProfile.company) {
        throw new Error('No company profile found');
      }
      
      // Get a reference to the company document
      const companyRef = doc(db, "companies", userProfile.company);
      
      // Update the document to remove the googleGmail field
      await updateDoc(companyRef, {
        googleGmail: deleteField()
      });
      
      // Update the company data in the context
      if (updateCompanyInfo) {
        await updateCompanyInfo();
      }
      
      setSuccess('Google Gmail disconnected successfully. Please reload the page to see the changes.');
      setShowReloadButton(true);
      setIsGmailConnecting(false);
    } catch (err) {
      console.error('Error disconnecting Google Gmail:', err);
      setError(`Failed to disconnect Google Gmail: ${err.message}`);
      setIsGmailConnecting(false);
    }
  };
  
  // Helper function to reload the page
  const handleReloadPage = () => {
    window.location.reload();
  };
  
  // Handle form submission
  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!companyName || !industry || !size) {
      return setError('Please fill out all required fields');
    }
    
    try {
      setError('');
      setSuccess('');
      setIsSaving(true);
      
      // Create company data object
      const companyData = {
        name: companyName,
        industry,
        size,
        description,
        mission,
        customLogoUrl
      };
      
      // Add Google Calendar tokens if connected
      if (isCalendarConnected && calendarTokens) {
        companyData.googleCalendar = calendarTokens;
      }
      
      // Add Google Gmail tokens if connected
      if (isGmailConnected && gmailTokens) {
        companyData.googleGmail = gmailTokens;
      }
      
      // If there's a company ID in the user profile, update it
      if (userProfile && userProfile.company) {
        companyData.id = userProfile.company;
      }
      
      // Use the context's updateCompanyInfo function to update or create in Firestore
      const success = await updateCompanyInfo(companyData);
      
      if (success) {
        setSuccess('Company information updated successfully');
        
        // Update local state to match the submitted data
        if (!isCalendarConnected) {
          setCalendarTokens(null);
        }
        
        if (!isGmailConnected) {
          setGmailTokens(null);
        }
        
        setTimeout(() => {
          setSuccess('');
          // Show celebration modal instead of closing immediately
          setShowCelebration(true);
        }, 1000);
      } else {
        setError('Failed to update company information');
      }
    } catch (err) {
      console.error('Error updating company info:', err);
      setError('Failed to update company information: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  }
  
  // Handle custom close with animation
  const handleClose = () => {
    // Step 1: Fade out the card
    setIsVisible(false);
    
    // Step 2: After card fades, fade out backdrop
    setTimeout(() => {
      setBackdropVisible(false);
      
      // Step 3: After backdrop fades, remove blur
      setTimeout(() => {
        setBlurApplied(false);
        
        // Step 4: Finally notify parent component after all animations finish
        setTimeout(() => {
          onClose();
        }, 80);
      }, 80);
    }, 150);
  };
  
  // Handle celebration modal close
  const handleCelebrationClose = () => {
    setShowCelebration(false);
    handleClose();
  };
  
  // Tab navigation component
  const TabNavigation = () => (
    <div className="border-b border-gray-200 mb-4">
      <nav className="-mb-px flex space-x-6" aria-label="Settings Tabs">
        <button
          onClick={() => setActiveTab('company')}
          className={`py-3 px-1 border-b-2 font-medium text-xs md:text-sm ${
            activeTab === 'company'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          } transition-all duration-200`}
        >
          Context
        </button>
        <button
          onClick={() => setActiveTab('integrations')}
          className={`py-3 px-1 border-b-2 font-medium text-xs md:text-sm ${
            activeTab === 'integrations'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          } transition-all duration-200`}
        >
          Integrations
        </button>
        <button
          onClick={() => setActiveTab('other')}
          className={`py-3 px-1 border-b-2 font-medium text-xs md:text-sm ${
            activeTab === 'other'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          } transition-all duration-200`}
        >
          Other Settings
        </button>
      </nav>
    </div>
  );
  
  // Company Info tab content
  const CompanyInfoTab = () => (
    <div className="space-y-2 md:space-y-4 flex-1 overflow-y-auto">
      <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 md:p-3 rounded-md group">
        <label htmlFor="company-name" className="block text-sm font-medium text-gray-700 mb-0.5 md:mb-1 font-heading group-hover:text-orange-600">
          Company Name <span className="text-red-500">*</span>
        </label>
        <input
          id="company-name"
          name="company-name"
          type="text"
          required
          className="appearance-none rounded-md relative block w-full px-3 py-1 md:py-1.5 border border-gray-300 placeholder-gray-500 text-gray-900 
          focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-all duration-300
          bg-white bg-opacity-30"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
        />
      </div>
      
      <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 md:p-3 rounded-md group">
        <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-0.5 md:mb-1 font-heading group-hover:text-orange-600">
          Industry <span className="text-red-500">*</span>
        </label>
        <select
          id="industry"
          name="industry"
          required
          className="mt-0.5 md:mt-1 block w-full py-1 md:py-1.5 px-3 border border-gray-300 bg-white bg-opacity-30 rounded-md shadow-sm 
          focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-all duration-300"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
        >
          <option value="">Select an industry</option>
          <option value="Technology">Technology</option>
          <option value="Healthcare">Healthcare</option>
          <option value="Finance">Finance</option>
          <option value="Retail">Retail</option>
          <option value="Manufacturing">Manufacturing</option>
          <option value="Education">Education</option>
          <option value="Other">Other</option>
        </select>
      </div>
      
      <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 md:p-3 rounded-md group">
        <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-0.5 md:mb-1 font-heading group-hover:text-orange-600">
          Company Size <span className="text-red-500">*</span>
        </label>
        <select
          id="size"
          name="size"
          required
          className="mt-0.5 md:mt-1 block w-full py-1 md:py-1.5 px-3 border border-gray-300 bg-white bg-opacity-30 rounded-md shadow-sm 
          focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-all duration-300"
          value={size}
          onChange={(e) => setSize(e.target.value)}
        >
          <option value="">Select company size</option>
          <option value="1-10">1-10 employees</option>
          <option value="11-50">11-50 employees</option>
          <option value="51-200">51-200 employees</option>
          <option value="201-500">201-500 employees</option>
          <option value="501-1000">501-1000 employees</option>
          <option value="1000+">1000+ employees</option>
        </select>
      </div>
      
      <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 md:p-3 rounded-md group">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-0.5 md:mb-1 font-heading group-hover:text-orange-600">
          Company Description
        </label>
        <textarea
          id="description"
          name="description"
          rows="2"
          className="shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 mt-0.5 md:mt-1 block w-full sm:text-sm border border-gray-300 
          rounded-md transition-all duration-300 bg-white bg-opacity-30 py-1 md:py-1.5 px-3"
          placeholder="Briefly describe what your company does"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        ></textarea>
      </div>
      
      <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 md:p-3 rounded-md group">
        <label htmlFor="mission" className="block text-sm font-medium text-gray-700 mb-0.5 md:mb-1 font-heading group-hover:text-orange-600">
          Company Mission
        </label>
        <textarea
          id="mission"
          name="mission"
          rows="2"
          className="shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 mt-0.5 md:mt-1 block w-full sm:text-sm border border-gray-300 
          rounded-md transition-all duration-300 bg-white bg-opacity-30 py-1 md:py-1.5 px-3"
          placeholder="What is your company's mission?"
          value={mission}
          onChange={(e) => setMission(e.target.value)}
        ></textarea>
      </div>
    </div>
  );
  
  // Other settings tab content with logo customization
  const OtherSettingsTab = () => (
    <div className="transition-all duration-300 flex-1 overflow-y-auto">
      <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-3 md:p-4 rounded-md mb-4">
        <h3 className="text-base font-medium text-gray-800 mb-2">Logo Customization</h3>
        
        <div className="mb-4 p-3 bg-white bg-opacity-30 rounded-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">Current Logo</label>
          <div className="flex items-center justify-center bg-gray-100 bg-opacity-60 rounded-md p-5 mb-2">
            <div className="h-24 w-48 flex items-center justify-center border border-gray-200 border-opacity-50 rounded bg-white bg-opacity-80 shadow-sm p-2">
              <EosLogo customLogoUrl={customLogoUrl} className="w-32" />
            </div>
          </div>
        </div>
        
        <div className="mb-4">
          <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 md:p-3 rounded-md group">
            <label className="block text-sm font-medium text-gray-700 mb-1 font-heading group-hover:text-orange-600">
              Upload Logo
            </label>
            <div className="mt-1 flex items-center">
              <label htmlFor="logo-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-orange-600 hover:text-orange-500 focus-within:outline-none">
                <span className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white bg-opacity-80 hover:bg-opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200">
                  {isLogoUploading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <svg className="-ml-1 mr-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload file
                    </>
                  )}
                </span>
                <input 
                  id="logo-upload" 
                  name="logo-upload" 
                  type="file" 
                  className="sr-only" 
                  accept="image/*" 
                  onChange={handleLogoUpload}
                  disabled={isLogoUploading || isSaving}
                />
              </label>
              <p className="ml-3 text-xs text-gray-500">PNG, JPG, GIF or SVG (max 2MB)</p>
            </div>
          </div>
        </div>
        
        <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 md:p-3 rounded-md group">
          <label htmlFor="custom-logo-url" className="block text-sm font-medium text-gray-700 mb-1 font-heading group-hover:text-orange-600">
            Or Enter Logo URL
          </label>
          <input
            id="custom-logo-url"
            name="custom-logo-url"
            type="text"
            className="appearance-none rounded-md relative block w-full px-3 py-1.5 border border-gray-300 placeholder-gray-500 text-gray-900 
            focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-all duration-300
            bg-white bg-opacity-30"
            value={customLogoUrl}
            onChange={(e) => setCustomLogoUrl(e.target.value)}
            placeholder="https://example.com/your-logo.png"
          />
          <p className="mt-1 text-xs text-gray-600">
            Enter the URL to your custom logo image. Leave blank to use the default EOS logo.
          </p>
        </div>
        
        <div className="mt-3 text-xs text-gray-600 p-2 bg-blue-50 bg-opacity-50 rounded border border-blue-100">
          <p>💡 <strong>Pro Tip:</strong> For the best appearance, use an image with transparent background in PNG format.</p>
        </div>
      </div>
      
      {/* Legal Links Section */}
      <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-3 md:p-4 rounded-md mb-4">
        <h3 className="text-base font-medium text-gray-800 mb-2">Legal</h3>
        
        <div className="p-3 bg-white bg-opacity-30 rounded-md">
          <ul className="space-y-3">
            <li>
              <a 
                href="/privacy-policy" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center text-sm text-orange-600 hover:text-orange-700 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Privacy Policy
              </a>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="bg-white bg-opacity-10 p-4 rounded-md border border-gray-200 border-opacity-30 flex flex-col items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <h3 className="text-base font-medium text-gray-800 mb-1">More Settings Coming Soon</h3>
        <p className="text-gray-600 text-center text-xs mb-2">
          Additional customization options will be available in future updates.
        </p>
        <span className="text-xs text-orange-600 font-medium">Stay tuned!</span>
      </div>
    </div>
  );
  
  // Integrations tab content with Google Calendar
  const IntegrationsTab = () => (
    <div className="transition-all duration-300 flex-1 overflow-y-auto">
      {/* Google Calendar Integration */}
      <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-3 md:p-4 rounded-md mb-4">
        <h3 className="text-base font-medium text-gray-800 mb-2">Google Calendar Integration</h3>
        
        <div className="mb-4 p-3 bg-white bg-opacity-30 rounded-md">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Calendar Connection Status:</span>
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
              isCalendarConnected 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {isCalendarConnected ? 'Connected' : 'Not Connected'}
            </span>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            Connect your Google Calendar to allow the EOS Assistant to check your upcoming events, including Level 10 meetings. 
            This integration enables the assistant to provide updates about your schedule when asked.
          </p>
          
          {isCalendarConnected ? (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
              onClick={handleDisconnectGoogleCalendar}
              disabled={isCalendarConnecting || isSaving}
            >
              {isCalendarConnecting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                  Disconnect Calendar
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              onClick={handleConnectGoogleCalendar}
              disabled={isCalendarConnecting || isSaving}
            >
              {isCalendarConnecting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Connecting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Connect Google Calendar
                </>
              )}
            </button>
          )}
          
          <p className="mt-3 text-xs text-gray-500">
            Your calendar data is only accessed when you specifically ask about your schedule in a chat. 
            You can disconnect access at any time.
          </p>
        </div>
        
        <div className="bg-yellow-50 bg-opacity-70 border-l-4 border-yellow-400 p-3 rounded-r">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Privacy Note:</strong> We only request access to read your calendar events. We will never modify or create events without your explicit permission.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Google Gmail Integration - Updated to match Calendar UI */}
      <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-3 md:p-4 rounded-md mb-4">
        <h3 className="text-base font-medium text-gray-800 mb-2">Google Gmail Integration</h3>
        
        <div className="mb-4 p-3 bg-white bg-opacity-30 rounded-md">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Gmail Connection Status:</span>
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
              isGmailConnected 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {isGmailConnected ? 'Connected' : 'Not Connected'}
            </span>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            Connect your Gmail account to allow the EOS Assistant to search and reference your emails. 
            This integration enables the assistant to find information from your inbox when asked about emails.
          </p>
          
          {isGmailConnected ? (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
              onClick={handleDisconnectGoogleGmail}
              disabled={isGmailConnecting || isSaving}
            >
              {isGmailConnecting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                  Disconnect Gmail
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              onClick={handleConnectGoogleGmail}
              disabled={isGmailConnecting || isSaving}
            >
              {isGmailConnecting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Connecting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                  Connect Gmail
                </>
              )}
            </button>
          )}
          
          <p className="mt-3 text-xs text-gray-500">
            Your email data is only accessed when you specifically ask about your emails in a chat. 
            You can disconnect access at any time.
          </p>
        </div>
        
        <div className="bg-yellow-50 bg-opacity-70 border-l-4 border-yellow-400 p-3 rounded-r">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Privacy Note:</strong> We only request access to read your emails. We will never modify, delete, or send emails without your explicit permission.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Placeholder for future integrations */}
      <div className="bg-white bg-opacity-10 p-4 rounded-md border border-gray-200 border-opacity-30 flex flex-col items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
        <h3 className="text-base font-medium text-gray-800 mb-1">More Integrations Coming Soon</h3>
        <p className="text-gray-600 text-center text-xs mb-2">
          Additional integrations with HubSpot, Slack, and more will be available in future updates.
        </p>
        <span className="text-xs text-orange-600 font-medium">Stay tuned!</span>
      </div>
    </div>
  );
  
  if (!shouldRender) return null;
  
  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center z-50">
        {/* Backdrop blur and slight darkening */}
        <div 
          className={`absolute inset-0 transition-all duration-300 ease-out ${
            blurApplied ? 'backdrop-filter backdrop-blur-md' : 'backdrop-filter backdrop-blur-none'
          } ${
            backdropVisible ? 'bg-gray-900 bg-opacity-10' : 'bg-opacity-0'
          }`}
          onClick={handleClose}
          style={{ transitionProperty: 'backdrop-filter, background-color, opacity' }}
        />
        
        {/* Glassmorphism modal with additional blur effect directly behind it */}
        <div 
          ref={modalRef}
          className={`w-full max-w-3xl mx-4 max-h-[90vh] md:max-h-[85vh] relative 
          bg-white bg-opacity-25 border border-white border-opacity-30
          shadow-xl rounded-2xl backdrop-filter backdrop-blur-xl
          transition-all duration-300 ease-out transform flex flex-col
          overflow-hidden
          ${isVisible ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'}`}
          style={{
            boxShadow: blurApplied ? '0 0 50px 20px rgba(255,255,255,0.2), 0 6px 20px rgba(0,0,0,0.1), 0 10px 40px 25px rgba(0,0,0,0.15)' : 'none',
            backdropFilter: 'blur(16px)'
          }}
        >
          {loading || companyLoading ? (
            <div className="flex items-center justify-center h-48 p-4">
              <div className="animate-pulse-slow rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mb-3"></div>
              <p className="text-gray-700 font-medium ml-3">Loading settings...</p>
            </div>
          ) : (
            <div className="p-3 md:p-5 overflow-y-auto flex-1 flex flex-col overflow-x-hidden">
              {/* Close button */}
              <button 
                onClick={handleClose}
                className="absolute top-2 md:top-3 right-2 md:right-3 text-gray-500 hover:text-gray-800 bg-white bg-opacity-50 rounded-full p-1.5 
                  hover:bg-opacity-80 transition-all duration-200 transform hover:rotate-90 btn-pop z-10"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="flex items-center mb-3 md:mb-4">
                <div className="transition-transform duration-300 transform hover:scale-105 mr-3 h-8 md:h-10 w-16 md:w-20 flex items-center justify-center">
                  <EosLogo customLogoUrl={customLogoUrl} />
                </div>
                <h2 className="text-lg md:text-xl font-bold text-gray-800 font-heading">Settings</h2>
              </div>
              
              {error && (
                <div className="bg-red-50 bg-opacity-70 border-l-4 border-red-500 text-red-700 p-2 md:p-3 mb-3 md:mb-4 rounded-r-md shadow-sm animate-fade-in" role="alert">
                  <p className="font-bold font-heading">Error</p>
                  <p>{error}</p>
                </div>
              )}
              
              {success && (
                <div className="bg-green-50 bg-opacity-70 border-l-4 border-green-500 text-green-700 p-2 md:p-3 mb-3 md:mb-4 rounded-r-md shadow-sm animate-fade-in" role="alert">
                  <p className="font-bold font-heading">Success</p>
                  <p>{success}</p>
                  {showReloadButton && (
                    <button 
                      onClick={handleReloadPage}
                      className="mt-4 inline-flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                      <LordIcon 
                        icon={autorenewIcon} 
                        size={24} 
                        trigger="hover"
                        colors="primary:#ffffff"
                        className="mr-2"
                      />
                      Reload Page
                    </button>
                  )}
                </div>
              )}
              
              <TabNavigation />
              
              <form onSubmit={handleSubmit} className="space-y-2 md:space-y-4 flex-1 flex flex-col">
                <div className="flex-1 overflow-y-auto pb-2 overflow-x-hidden">
                  {/* Tab content based on active tab */}
                  {activeTab === 'company' && <CompanyInfoTab />}
                  {activeTab === 'integrations' && <IntegrationsTab />}
                  {activeTab === 'other' && <OtherSettingsTab />}
                </div>
                
                <div className="flex-shrink-0 mt-2 md:mt-4 pt-2 md:pt-3 border-t border-white border-opacity-20 flex flex-col">
                  <div className="flex justify-end pb-safe">
                    <button
                      type="submit"
                      disabled={isSaving || isLogoUploading}
                      className="py-1 md:py-1.5 px-4 md:px-5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-eos-orange hover:shadow-eos-lg 
                      transition-all duration-300 flex items-center btn-pop min-h-[40px] md:min-h-[44px]"
                    >
                      {isSaving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm md:text-base">Saving...</span>
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm md:text-base">Save Changes</span>
                        </>
                      )}
                    </button>
                  </div>
                  
                  <div className="text-center mt-1 md:mt-2">
                    <p className="text-xs text-gray-600 animate-pulse-slow">
                      Powered by the Entrepreneurial Operating System®
                    </p>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
      {/* Celebration modal */}
      <CelebrationModal 
        isOpen={showCelebration} 
        onClose={handleCelebrationClose} 
        userName={userProfile?.displayName} 
      />
    </>
  );
}

export default SettingsModal; 